import { useEffect, useState, useCallback, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'store';
import {
  GridColDef,
  GridToolbar,
  GridInitialState,
  useGridApiRef,
  GridActionsCellItem,
  GridRowParams,
} from '@mui/x-data-grid-pro';
import { ResponsiveDataGrid } from 'ui-component/ResponsiveDataGrid';
import { Box,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import ConfirmationDialog from 'ui-component/ConfirmationDialog';
import { useTheme } from '@mui/material/styles';
import { IRoute } from './../../../../types/route.interface';
import { UseRoutesState } from 'hooks/useRoutes.hooks';
import { openSnackbar } from 'store/slices/snackbar';
import DomPurify from 'dompurify';
import EditIcon from '@mui/icons-material/Edit';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';

export default function RoutesDataGrid({routesState, route, setRoute, organizationState, active = true} : {routesState: UseRoutesState, route: IRoute, setRoute: (route: IRoute) => void, organizationState: any, active?: boolean}) {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showArchiveConfirmation, setShowArchiveConfirmation] = useState(false);
  const [showReactivateConfirmation, setShowReactivateConfirmation] = useState(false);
  const [showPermanentlyDeleteConfirmation, setShowPermanentlyDeleteConfirmation] = useState(false);
  const [showDuplicateConfirmation, setShowDuplicateConfirmation] = useState(false);
  const [activeRouteID, setActiveRouteID] = useState(-1);
  const [recurringRoute, setRecurringRoute] = useState<any>(false);
  const [hasUpcomingRoute, setHasUpcomingRoute] = useState<boolean>(false);
  const apiRef = useGridApiRef();
  const [initialState, setInitialState] = useState<GridInitialState>();

  const saveSnapshot = useCallback(() => {
    if (apiRef?.current?.exportState && localStorage) {
      const currentState = apiRef.current.exportState();
      localStorage.setItem('adminRoutesDataGridState', JSON.stringify(currentState));
    }
  }, [apiRef]);

  useLayoutEffect(() => {
    const stateFromLocalStorage = localStorage?.getItem('adminRoutesDataGridState');
    setInitialState(stateFromLocalStorage ? JSON.parse(stateFromLocalStorage) : {});

    // handle refresh and navigating away/refreshing
    window.addEventListener('beforeunload', saveSnapshot);

    return () => {
      // in case of an SPA remove the event-listener
      window.removeEventListener('beforeunload', saveSnapshot);
      saveSnapshot();
    };
  }, [saveSnapshot]);

  const columns: GridColDef[] = [
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 180,
      cellClassName: 'actions',
      getActions: (params: GridRowParams) => {
        if (active) {
          return [
            <GridActionsCellItem
              icon={<Tooltip title="Edit"><EditIcon /></Tooltip>}
              label="Edit"
              className="textPrimary"
              onClick={() => navigate('/routes/edit/' + params.row.id)}
              color="inherit"
            />,
            <GridActionsCellItem
              icon={<Tooltip title="Archive"><ArchiveOutlinedIcon /></Tooltip>}
              label="Archive"
              onClick={() => {
                setActiveRouteID(params.row.id as number);
                setRecurringRoute(params.row.repeats);
                const upcoming = params.row.upcoming_instances_count ?? 0;
                setHasUpcomingRoute(upcoming > 0);
                setShowArchiveConfirmation(true);
              }}
              color="inherit"
            />,
            <GridActionsCellItem
              icon={<Tooltip title="Duplicate"><ContentCopyIcon /></Tooltip>}
              label="Duplicate"
              onClick={() => {
                setActiveRouteID(params.row.id as number);
                setShowDuplicateConfirmation(true);
              }}
              color="inherit"
            />,
          ];
        }
        return [
          <GridActionsCellItem
            icon={<Tooltip title="Reactivate"><UnarchiveOutlinedIcon /></Tooltip>}
            label="Reactivate"
            onClick={() => {
              setActiveRouteID(params.row.id as number);
              setRecurringRoute(params.row.repeats);
              const upcoming = params.row.upcoming_instances_count ?? 0;
              setHasUpcomingRoute(upcoming > 0);
              setShowReactivateConfirmation(true);
            }}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<Tooltip title="Delete"><DeleteOutlinedIcon /></Tooltip>}
            label="Delete"
            onClick={() => {
              setActiveRouteID(params.row.id as number);
              setShowPermanentlyDeleteConfirmation(true);
            }}
            color="inherit"
          />,
        ];
      },
    },
    { field: 'program', 
      headerName: 'Program',
      width: 160, 
      type: 'string',
      valueGetter: (value, row) => {return row.program.name},
    },
    { field: 'regularDayOfWeekNumber',
      headerName: 'Day of Week',
      width: 160,
      type: "singleSelect",
      valueOptions: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday', 'Multiple/Variable'],
      valueGetter: (value, row) => {
        return getRegularDayOfWeek(row.regularDayOfWeekNumber);
      },
      valueFormatter: (value?: string) => {
        return value;
      },
    },
    { field: 'pickUp', headerName: 'Food Donor', width: 350,
          valueGetter: (value, row) =>
        {
          return `${(row.pick_up_locations.map((a : any) =>
            a.storage_facility ? `${a.first_name} - Temporary Storage Facility` : a.first_name + (a.last_name ? ' ' + a.last_name : '')
          )).join(", ")}`},
    },
    { field: 'dropOff', headerName: 'Food Recipient', width: 350,
          valueGetter: (value, row) =>
        {
          return `${(row.drop_off_locations.map((a : any) =>
            a.storage_facility ? `${a.first_name} - Temporary Storage Facility` : a.first_name
          )).join(", ")}`},
    },
    { field: 'description', 
      headerName: 'Route-Specific Description',
      width: 180,
      renderCell: (params) => (
        <div
          style={{ whiteSpace: 'pre-line' }}
          dangerouslySetInnerHTML={{
            __html: DomPurify.sanitize(params.value ?? '')
          }}
        />
      ) 
    },
    {
      field: 'numberLocations',
      headerName: '# Locations',
      type: 'number',
      width: 100,
      valueGetter: (value, row) =>
        {
          return `${row.pick_up_locations.length + row.drop_off_locations.length}`;
        },
    },
    ...(active ? [{
      field: 'instances_filled',
      headerName: 'Instances Filled',
      sortable: true,
      width: 120,
      valueGetter: (value: any, row: any) =>
        `${row.filled_instances_count}/${row.total_instances_count}`,
    }] : []),
    {
      field: 'id',
      headerName: 'ID',
      type: 'string',
      width: 100,
      editable: false,
    },
  ];

  function getRegularDayOfWeekNumber(regularDayOfWeek: string) {
    if (regularDayOfWeek == 'Monday') {
      return 0;
    } else if (regularDayOfWeek == 'Tuesday') {
      return 1;
    } else if (regularDayOfWeek == 'Wednesday') {
      return 2;
    } else if (regularDayOfWeek == 'Thursday') {
      return 3;
    } else if (regularDayOfWeek == 'Friday') {
      return 4;
    } else if (regularDayOfWeek == 'Saturday') {
      return 5;
    } else if (regularDayOfWeek == 'Sunday') {
      return 6;
    } else { // Multiple/Variable
      return 7;
    }
  }

  function getRegularDayOfWeek(regularDayOfWeekNumber: number) {
    if (regularDayOfWeekNumber == 0) {
      return 'Monday';
    } else if (regularDayOfWeekNumber == 1) {
      return 'Tuesday';
    } else if (regularDayOfWeekNumber == 2) {
      return 'Wednesday';
    } else if (regularDayOfWeekNumber == 3) {
      return 'Thursday';
    } else if (regularDayOfWeekNumber == 4) {
      return 'Friday';
    } else if (regularDayOfWeekNumber == 5) {
      return 'Saturday';
    } else if (regularDayOfWeekNumber == 6) {
      return 'Sunday';
    } else {
      return 'Multiple/Variable';
    }
  }


  const [rows, setRows] = useState<IRoute[]>([]);
  let routesArray : IRoute[];
  useEffect(() => {
    if (routesState?.routes && organizationState.ready) {
      routesArray = routesState.routes.map((route : any)=>{
        route.regularDayOfWeekNumber = getRegularDayOfWeekNumber(route.regularDayOfWeek);
        return route;
      })
      setRows(routesArray);
    
    }
  }, [routesState?.routes, organizationState.ready]);

  const onRowsSelectionHandler = (ids: any) => {
    const selectedRowsData = ids.map((id: any) => rows.find((row) => row.id === id));
    setRoute(selectedRowsData[0]);
  };

  const cancelDialog = () => {
    setShowArchiveConfirmation(false);
    setShowReactivateConfirmation(false);
    setShowPermanentlyDeleteConfirmation(false);
    setShowDuplicateConfirmation(false);
  }

  const confirmArchive = async (sendNotification?: boolean) => {
    await routesState.archiveRoute(activeRouteID, sendNotification);
    dispatch(
        openSnackbar({
            open: true,
            message: 'Route has been successfully archived.',
            variant: 'alert',
            alert: {
                color: 'success'
            },
            close: false
        })
    );
    setShowArchiveConfirmation(false);
  };

  const confirmReactivate = async (sendNotification?: boolean) => {
    await routesState.reactivateRoute(activeRouteID, sendNotification);
    dispatch(
        openSnackbar({
            open: true,
            message: 'Route has been successfully reactivated.',
            variant: 'alert',
            alert: {
                color: 'success'
            },
            close: false
        })
    );
    setShowReactivateConfirmation(false);
  };

  const confirmPermanentlyDelete = async () => {
    await routesState.permanentlyDeleteRoute(activeRouteID);
    dispatch(
        openSnackbar({
            open: true,
            message: 'Route has been successfully permanently deleted.',
            variant: 'alert',
            alert: {
                color: 'success'
            },
            close: false
        })
    );
    setShowPermanentlyDeleteConfirmation(false);
  };

  const confirmDuplicate = async () => {
    await routesState.duplicateRoute(activeRouteID);
    setShowDuplicateConfirmation(false);
  };

  if (!routesState?.routes) {
    return (
      <div>Loading...</div>
  )
  }

  // Handle saved table views
  if (!initialState) {
    return <CircularProgress />;
  }
  
  return (
    <div style={{ height: 700, width: '100%' }}>
      <Box
        sx={{
        height: 700,
        width: '100%',
        '& .actions': {
          color: 'text.secondary',
        },
        '& .textPrimary': {
          color: 'text.primary',
        },
        '& .MuiDataGrid-root': {
            border: 'none',
            '& .MuiDataGrid-cell': {
                borderColor: theme.palette.mode === 'dark' ? theme.palette.text.primary + 15 : 'grey.200'
            },
            '& .MuiDataGrid-columnsContainer': {
                color: theme.palette.mode === 'dark' ? 'grey.600' : 'grey.900',
                borderColor: theme.palette.mode === 'dark' ? theme.palette.text.primary + 15 : 'grey.200'
            },
            '& .MuiDataGrid-columnSeparator': {
                color: theme.palette.mode === 'dark' ? theme.palette.text.primary + 15 : 'grey.200'
            }
          },
          '& .MuiDataGrid-columnHeader, .MuiDataGrid-cell': {
            borderRight: `1px solid ${
              theme.palette.mode === 'light' ? '#f0f0f0' : '#303030'
            }`,
          },
      }}
      >
      <ResponsiveDataGrid
      rows={rows} 
      columns={columns}
      pagination
      slots={{ toolbar: GridToolbar }}
      rowSelectionModel={route ? [route.id] : []}
      onRowSelectionModelChange={(ids : any) => onRowsSelectionHandler(ids)}
      apiRef={apiRef}
      initialState={{
        ...initialState,
      }}
      />
      <ConfirmationDialog
          open={showArchiveConfirmation}
          onCancel={cancelDialog}
          onConfirm={confirmArchive}
          title="Archive Route?"
          message="Confirm you want to archive this route?"
          showNotificationCheckbox={recurringRoute && hasUpcomingRoute}
          notificationCheckboxLabel="Notify participants"
          notificationCheckboxTooltip="Send notifications to food donors, recipients, and delivery drivers about this route cancellation"
        />
      <ConfirmationDialog
          open={showReactivateConfirmation}
          onCancel={cancelDialog}
          onConfirm={confirmReactivate}
          title="Reactivate Route?"
          message="Confirm you want to reactivate this route?"
          showNotificationCheckbox={recurringRoute && hasUpcomingRoute}
          notificationCheckboxLabel="Notify participants"
          notificationCheckboxTooltip="Send notifications to food donors, recipients, and delivery drivers about this route reactivation"
      />
      <ConfirmationDialog
          open={showPermanentlyDeleteConfirmation}
          onCancel={cancelDialog}
          onConfirm={confirmPermanentlyDelete}
          title="Permanently Delete Route?"
          message="Confirm you want to permanently delete this route? This action cannot be undone and will impact reports and metrics."
      />
      <ConfirmationDialog
          open={showDuplicateConfirmation}
          onCancel={cancelDialog}
          onConfirm={confirmDuplicate}
          title="Duplicate Route?"
          message="Confirm you want to duplicate this route?"
      />
      </Box>
    </div>
  );
}